import React, {useState} from 'react';

import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';

import DynamicAvatar from '../../Shared/DynamicAvatar';
import {getGroupColor} from '../../../Utilities/ColorUtility';

const labelClass = 'text-[11px] font-semibold text-main-normal tracking-[0.5px]';
const tooltipSx = {tooltip: {className: '!text-base !text-main-normal !bg-background-normal !border !border-panels-normal !shadow-lg !rounded-input'}};
const MAX_DISPLAY = 3;
const AVATAR_SIZE = 28;

const xBadgeStyle = {
    position: 'absolute', top: -4, right: -4,
    width: 14, height: 14, borderRadius: '50%',
    backgroundColor: 'var(--leavedeletenew-normal-color)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', zIndex: 10,
};

const GroupChip = ({group, onRemove}) => {
    const [hovered, setHovered] = useState(false);
    const color = getGroupColor(group.GroupId || group.GroupName || '');
    const bgColor = color.replace('rgb(', 'rgba(').replace(')', ', 0.15)');
    const memberCount = group.MemberCount || group.memberCount;
    const tooltipText = memberCount != null ? `${group.GroupName} (${memberCount} members)` : group.GroupName;
    return (
        <Tooltip title={tooltipText} placement="top" componentsProps={tooltipSx}>
            <div
                style={{width: AVATAR_SIZE, height: AVATAR_SIZE, position: 'relative', overflow: 'visible'}}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                <div className="flex items-center justify-center rounded-full" style={{width: AVATAR_SIZE, height: AVATAR_SIZE, backgroundColor: bgColor}}>
                    <Groups2OutlinedIcon style={{fontSize: 16, color}} />
                </div>
                {hovered && onRemove && (
                    <div onClick={(e) => { e.stopPropagation(); onRemove(); }} style={xBadgeStyle}>
                        <CloseIcon style={{fontSize: 10, color: '#fff'}} />
                    </div>
                )}
            </div>
        </Tooltip>
    );
};

const MemberChip = ({member, onRemove}) => {
    const [hovered, setHovered] = useState(false);
    const fullName = [member.FirstName, member.LastName].filter(Boolean).join(' ');
    return (
        <Tooltip title={fullName || member.Email || ''} placement="top" componentsProps={tooltipSx}>
            <div
                style={{width: AVATAR_SIZE, height: AVATAR_SIZE, position: 'relative', overflow: 'visible'}}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                <DynamicAvatar
                    user={{Name: member.FirstName, LastName: member.LastName}}
                    imageSrc={member.UserIcon}
                    style={{width: AVATAR_SIZE, height: AVATAR_SIZE, fontSize: 11}}
                />
                {hovered && onRemove && (
                    <div onClick={(e) => { e.stopPropagation(); onRemove(); }} style={xBadgeStyle}>
                        <CloseIcon style={{fontSize: 10, color: '#fff'}} />
                    </div>
                )}
            </div>
        </Tooltip>
    );
};

const buildItems = (groups, members) => [
    ...groups.map((g) => ({key: g.GroupId, type: 'group', data: g})),
    ...members.map((m) => ({key: m._id, type: 'member', data: m})),
];

const InvitedMembersRow = ({groups, members, onAdd, onRemove, addRef, isAccessLoaded = true}) => {
    const items = buildItems(groups, members);
    const displayed = items.slice(0, MAX_DISPLAY);
    const extraCount = items.length - MAX_DISPLAY;
    const canAdd = isAccessLoaded && onAdd;

    return (
        <div className="flex items-center justify-between mt-3" style={{overflow: 'visible'}}>
            <span data-testid="settings-invite-count" className={labelClass}>INVITED MEMBERS ({items.length})</span>
            <div className="flex items-center -space-x-1.5" style={{overflow: 'visible'}}>
                {displayed.map((item) => item.type === 'group'
                    ? <GroupChip key={item.key} group={item.data} onRemove={onRemove ? () => onRemove(item.key) : null} />
                    : <MemberChip key={item.key} member={item.data} onRemove={onRemove ? () => onRemove(item.key) : null} />
                )}
                {extraCount > 0 && (
                    <div className="flex items-center justify-center rounded-full bg-panels-normal text-main-normal font-medium" style={{width: AVATAR_SIZE, height: AVATAR_SIZE, fontSize: 11}}>
                        +{extraCount}
                    </div>
                )}
                <div data-testid="settings-invite-add" data-ready={isAccessLoaded ? 'true' : 'false'} ref={addRef} className={`flex items-center justify-center rounded-full border border-panels-normal ml-2 ${canAdd ? 'cursor-pointer hover:bg-panels-normal' : 'opacity-50 cursor-not-allowed'}`} style={{width: AVATAR_SIZE, height: AVATAR_SIZE}} onClick={canAdd ? onAdd : undefined}>
                    <PersonAddAltOutlinedIcon className="text-main-hover" style={{fontSize: 14}} />
                </div>
            </div>
        </div>
    );
};

export default InvitedMembersRow;
